import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import { localize } from 'wanderu-i18n';
import {
    AC,
    Attendant,
    ExtraLegroom,
    Games,
    Green,
    HotBeverage,
    LoyaltyRewards,
    Meal,
    Movies,
    PowerOutlet,
    Restroom,
    Newspaper,
    SatelliteTV,
    Seatbelts,
    Snacks,
    USBPower,
    Water,
    Wifi,
} from 'wanderu-icons';

import Chip from '../Chip/Chip';

import styles from './Features.scss';

const FEATURE_MAP = {
    air_conditioning: AC,
    attendant: Attendant,
    extra_legroom: ExtraLegroom,
    games: Games,
    green_cert: Green,
    beverage: HotBeverage,
    hot_beverage: HotBeverage,
    meal: Meal,
    loyalty_reward: LoyaltyRewards,
    movie: Movies,
    power_outlet: PowerOutlet,
    toilets: Restroom,
    bathroom: Restroom,
    toilet: Restroom,
    newspaper_magazine: Newspaper,
    satellite_tv: SatelliteTV,
    seat_belts: Seatbelts,
    snacks: Snacks,
    usb_power: USBPower,
    free_water: Water,
    wifi: Wifi,
};

function FeatureIcon(props) {
    const {
        t,
        featureNameStyle,
        feature,
        showFeatureName,
        useChip,
        featureChipClassName,
    } = props;
    const containerClassName = classNames(
        styles.feature,
        featureNameStyle,
        { [styles.withName]: showFeatureName },
    );
    const iconClassName = classNames(
        styles.img,
        { [styles.withName]: showFeatureName },
    );
    const featureString = t(`baseApp:${feature}_features`);
    const Icon = FEATURE_MAP[feature];

    if (!Icon) {
        return null;
    }

    let featureContent;
    if (useChip) {
        featureContent = (
            <Chip
                className={featureChipClassName}
                leftIcon={
                    <Icon
                        className={iconClassName}
                        title={featureString}
                    />
                }
                text={featureString}
            />
        );
    } else {
        featureContent = (
            <React.Fragment>
                <Icon
                    className={iconClassName}
                    title={featureString}
                />
                {showFeatureName && (
                    <span className={styles.name}>
                        {featureString}
                    </span>
                )}
            </React.Fragment>
        );
    }

    return (
        <span
            className={containerClassName}
            role="listitem"
        >
            {featureContent}
        </span>
    );
}

const LocalizedFeatureIcon = localize(FeatureIcon);

function Features(props) {
    const {
        className,
        features,
        featureNameStyle,
        showFeatureNameNextToIcon,
        useChip,
        featureChipClassName,
    } = props;

    const featuresWithIcons = features.filter((feature) => FEATURE_MAP[feature]);

    return (
        <div
            className={`${styles.featuresContainer} ${className}`}
            role="list"
        >
            {featuresWithIcons.map((feature) => (
                <LocalizedFeatureIcon
                    feature={feature}
                    featureChipClassName={featureChipClassName}
                    featureNameStyle={featureNameStyle}
                    key={feature}
                    showFeatureName={showFeatureNameNextToIcon}
                    useChip={useChip}
                />
            ))}
        </div>
    );
}

Features.propTypes = {
    className: PropTypes.string,
    featureNameStyle: PropTypes.string,
    features: PropTypes.arrayOf(PropTypes.string).isRequired,
    showFeatureNameNextToIcon: PropTypes.bool,
    useChip: PropTypes.bool,
    featureChipClassName: PropTypes.string,
};

Features.defaultProps = {
    className: '',
    featureNameStyle: '',
    showFeatureNameNextToIcon: false,
    useChip: false,
    featureChipClassName: '',
};

export {
    LocalizedFeatureIcon as FeatureIcon,
    Features,
};
export default Features;
